import { useState } from 'react';
import { Check, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Layout } from '@/components/Layout';
import { useAppContext } from '@/hooks/useAppContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';
import { useThemeDefinitions } from '@/hooks/useDittoTheme';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { cn } from '@/lib/utils';

function RelaySettings() {
  const { config, updateConfig } = useAppContext();
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const { toast } = useToast();
  const [newRelay, setNewRelay] = useState('');
  const [publishing, setPublishing] = useState(false);

  const relays = config.relayMetadata.relays;

  const save = (updated: { url: string; read: boolean; write: boolean }[]) => {
    updateConfig((current) => ({
      ...current,
      relayMetadata: { relays: updated, updatedAt: Math.floor(Date.now() / 1000) },
    }));
  };

  const addRelay = () => {
    let url = newRelay.trim();
    if (!url) return;
    if (!url.startsWith('wss://') && !url.startsWith('ws://')) {
      url = `wss://${url}`;
    }
    try {
      new URL(url);
    } catch {
      toast({ title: 'Invalid relay URL', variant: 'destructive' });
      return;
    }
    if (relays.some(r => r.url === url)) {
      toast({ title: 'Relay already added', variant: 'destructive' });
      return;
    }
    save([...relays, { url, read: true, write: true }]);
    setNewRelay('');
  };

  const publish = async () => {
    if (!user) return;
    setPublishing(true);
    try {
      await createEvent({
        kind: 10002,
        content: '',
        tags: relays.map(r => {
          const tag = ['r', r.url];
          if (r.read && !r.write) tag.push('read');
          else if (r.write && !r.read) tag.push('write');
          return tag;
        }),
      });
      toast({ title: 'Relay list published', description: 'Your NIP-65 relay list (kind 10002) was published.' });
    } catch (err) {
      toast({
        title: 'Failed to publish relay list',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Relays</CardTitle>
        <CardDescription>
          Nostr relays used for reading and publishing events (NIP-65).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {relays.map((relay) => (
          <div key={relay.url} className="flex items-center gap-3">
            <span className="min-w-0 flex-1 truncate font-mono text-xs">{relay.url}</span>
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Read
              <Switch
                checked={relay.read}
                onCheckedChange={(checked) => {
                  save(relays.map(r => r.url === relay.url ? { ...r, read: checked } : r));
                }}
                aria-label={`Read from ${relay.url}`}
              />
            </Label>
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Write
              <Switch
                checked={relay.write}
                onCheckedChange={(checked) => {
                  save(relays.map(r => r.url === relay.url ? { ...r, write: checked } : r));
                }}
                aria-label={`Write to ${relay.url}`}
              />
            </Label>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={() => save(relays.filter(r => r.url !== relay.url))}
              aria-label={`Remove ${relay.url}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <div className="flex gap-2 pt-1">
          <Input
            placeholder="wss://relay.example.com"
            value={newRelay}
            onChange={(e) => setNewRelay(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addRelay(); }}
          />
          <Button onClick={addRelay} variant="secondary" className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>

        {user && (
          <Button onClick={publish} disabled={publishing} variant="outline" className="gap-1.5">
            <RefreshCw className={publishing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Publish relay list (kind 10002)
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function BlossomSettings() {
  const { config, updateConfig } = useAppContext();
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const { toast } = useToast();
  const [newServer, setNewServer] = useState('');
  const [publishing, setPublishing] = useState(false);

  const servers = config.blossomServerMetadata.servers;

  const save = (updated: string[]) => {
    updateConfig((current) => ({
      ...current,
      blossomServerMetadata: { servers: updated, updatedAt: Math.floor(Date.now() / 1000) },
    }));
  };

  const addServer = () => {
    let url = newServer.trim();
    if (!url) return;
    if (!url.startsWith('https://') && !url.startsWith('http://')) {
      url = `https://${url}`;
    }
    try {
      new URL(url);
    } catch {
      toast({ title: 'Invalid server URL', variant: 'destructive' });
      return;
    }
    if (!url.endsWith('/')) url += '/';
    if (servers.includes(url)) {
      toast({ title: 'Server already added', variant: 'destructive' });
      return;
    }
    save([...servers, url]);
    setNewServer('');
  };

  const publish = async () => {
    if (!user) return;
    setPublishing(true);
    try {
      await createEvent({
        kind: 10063,
        content: '',
        tags: servers.map(url => ['server', url]),
      });
      toast({ title: 'Blossom server list published', description: 'Your kind 10063 server list was published.' });
    } catch (err) {
      toast({
        title: 'Failed to publish server list',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Blossom servers</CardTitle>
        <CardDescription>
          Media servers your uploads are sent to. Videos are uploaded to all
          servers simultaneously for redundancy.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
          <Label htmlFor="use-app-blossom" className="cursor-pointer font-normal">
            <span className="font-medium">Include VidStr default servers</span>
            <span className="block text-xs text-muted-foreground mt-0.5">
              blossom.ditto.pub, blossom.dreamith.to, blossom.primal.net
            </span>
          </Label>
          <Switch
            id="use-app-blossom"
            checked={config.useAppBlossomServers}
            onCheckedChange={(checked) => {
              updateConfig((current) => ({ ...current, useAppBlossomServers: checked }));
            }}
          />
        </div>

        {servers.map((server) => (
          <div key={server} className="flex items-center gap-3">
            <span className="min-w-0 flex-1 truncate font-mono text-xs">{server}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={() => save(servers.filter(s => s !== server))}
              aria-label={`Remove ${server}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {servers.length === 0 && (
          <p className="text-sm text-muted-foreground">No custom servers added.</p>
        )}

        <div className="flex gap-2 pt-1">
          <Input
            placeholder="https://blossom.example.com"
            value={newServer}
            onChange={(e) => setNewServer(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addServer(); }}
          />
          <Button onClick={addServer} variant="secondary" className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>

        {user && (
          <Button onClick={publish} disabled={publishing} variant="outline" className="gap-1.5">
            <RefreshCw className={publishing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Publish server list (kind 10063)
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ThemeCard({
  title,
  description,
  authorPubkey,
  colors,
  active,
  onSelect,
}: {
  title: string;
  description?: string;
  authorPubkey?: string;
  colors: { background: string; text: string; primary: string };
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'group relative flex flex-col gap-2 rounded-lg border p-3 text-left transition-colors',
        active
          ? 'border-primary ring-2 ring-primary/40'
          : 'border-border hover:border-foreground/30',
      )}
    >
      {active && (
        <span className="absolute right-2 top-2 rounded-full bg-primary p-0.5 text-primary-foreground">
          <Check className="h-3.5 w-3.5" />
        </span>
      )}
      {/* Mini preview */}
      <span
        className="flex h-16 w-full items-center justify-center gap-1.5 overflow-hidden rounded-md border border-border"
        style={{ backgroundColor: colors.background }}
      >
        <span
          className="rounded px-2 py-0.5 text-[10px] font-semibold"
          style={{ backgroundColor: colors.primary, color: colors.background }}
        >
          Aa
        </span>
        <span className="text-[10px] font-medium" style={{ color: colors.text }}>
          Aa
        </span>
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{title}</span>
        {description && (
          <span className="block truncate text-xs text-muted-foreground">{description}</span>
        )}
        {authorPubkey && <ThemeAuthor pubkey={authorPubkey} />}
      </span>
      <span className="flex gap-1">
        {[colors.background, colors.text, colors.primary].map((c) => (
          <span
            key={c}
            className="inline-block h-3.5 w-3.5 rounded-full border border-border"
            style={{ backgroundColor: c }}
            title={c}
          />
        ))}
      </span>
    </button>
  );
}

function ThemeAuthor({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const name = author.data?.metadata?.name ?? genUserName(pubkey);
  return (
    <span className="block truncate text-xs text-muted-foreground">by {name}</span>
  );
}

function AppearanceSettings() {
  const { theme, setTheme } = useTheme();
  const { config, updateConfig } = useAppContext();
  const themes = useThemeDefinitions();

  const activeAddr = config.dittoThemeAddr ?? '';

  const select = (naddr: string) => {
    updateConfig((current) => ({ ...current, dittoThemeAddr: naddr }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
        <CardDescription>Theme and colors.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <Label className="font-medium">Color mode</Label>
          <div className="flex gap-1">
            {(['light', 'dark', 'system'] as const).map((t) => (
              <Button
                key={t}
                variant={theme === t ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTheme(t)}
                className="capitalize"
              >
                {t}
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-3 border-t border-border pt-4">
          <div>
            <Label className="font-medium">App theme</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Ditto themes (kind 36767) found on your relays. VidStr applies
              their colors and fonts — not Ditto's rounded UI elements.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            <ThemeCard
              title="VidStr Default"
              description="The built-in theme"
              colors={
                theme === 'light'
                  ? { background: '#ffffff', text: '#0f0f0f', primary: '#e01e1e' }
                  : { background: '#0d0d0d', text: '#f5f5f5', primary: '#f02020' }
              }
              active={!activeAddr}
              onSelect={() => select('')}
            />
            {themes.data?.map((t) => (
              <ThemeCard
                key={t.naddr}
                title={t.title ?? t.dTag}
                description={t.description}
                authorPubkey={t.pubkey}
                colors={t.colors}
                active={activeAddr === t.naddr}
                onSelect={() => select(t.naddr)}
              />
            ))}
          </div>

          {themes.isLoading && (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full rounded-lg" />
              ))}
            </div>
          )}

          {!themes.isLoading && (themes.data?.length ?? 0) === 0 && (
            <p className="text-xs text-muted-foreground">
              No shared themes found on your relays yet. Themes are published
              as kind 36767 events (e.g. by Ditto).
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-3 sm:px-6 py-8 space-y-6">
        <h1 className="text-2xl font-bold">Settings</h1>
        <RelaySettings />
        <BlossomSettings />
        <AppearanceSettings />
      </div>
    </Layout>
  );
}
